import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { money, PAYMENT_LABELS, gradient } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, ScanLine, Plus, Minus, Trash2, User, Users, UserPlus, X, Tag, Gift, Zap,
  ExternalLink, Printer, ShoppingCart, BadgeCheck, CircleDollarSign, Cake,
} from "lucide-react";
import { toast } from "sonner";
import { FLOWER_WEIGHTS } from "@/lib/flowerTiers";

const EMPTY_CUST = { name: "", dob: "", phone: "", email: "", id_number: "", loyalty: false };

const TIERS_REMOVED = null;
const DISPLAY_TOKEN = "grove-st-main";

export default function POS() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [flower, setFlower] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [totals, setTotals] = useState({ subtotal: 0, total: 0, discount_amount: 0, split_oz_discount: 0, happy_hour_eligible: [], happy_hour_active: false });
  const [customer, setCustomer] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [hhFree, setHhFree] = useState([]);
  const scanRef = useRef(null);
  const [orderStarted, setOrderStarted] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [custMode, setCustMode] = useState("search");
  const openCust = (mode) => { setCustMode(mode); setCustOpen(true); };
  const onSelectCustomer = (c) => { setCustomer(c); setOrderStarted(true); setCustOpen(false); };

  const load = useCallback(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/flower-strains").then((r) => setFlower(r.data)).catch(() => {});
    api.get("/cash-drawer/current").then((r) => setDrawer(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // re-price on cart/discount change
  useEffect(() => {
    if (cart.length === 0) {
      setTotals({ subtotal: 0, total: 0, discount_amount: 0, split_oz_discount: 0, happy_hour_eligible: [], happy_hour_active: false });
      return;
    }
    const body = { items: cart, discount: discount || undefined };
    const t = setTimeout(() => {
      api.post("/pos/price-cart", body).then((r) => setTotals(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [cart, discount]);

  const hhAmount = useMemo(
    () => hhFree.reduce((s, ref) => s + (cart.find((c) => c.variant_id === ref)?.unit_price || 0), 0),
    [hhFree, cart]
  );
  const finalTotal = Math.max(0, (totals.total || 0) - hhAmount);

  // push to customer display
  useEffect(() => {
    api.post("/pos/display", {
      token: DISPLAY_TOKEN,
      cart: {
        items: cart.map((c) => ({ name: c.name, unit_price: c.unit_price, qty: c.qty })),
        subtotal: totals.subtotal, discount: (totals.discount_amount || 0) + (totals.split_oz_discount || 0) + hhAmount,
        total: finalTotal,
      },
    }).catch(() => {});
  }, [cart, totals, hhAmount, finalTotal]);

  const addToCart = (item) => {
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.variant_id === item.variant_id && c.weight_key === item.weight_key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...prev, { ...item, qty: 1 }];
    });
  };
  const setQty = (i, delta) =>
    setCart((prev) => prev.map((c, idx) => (idx === i ? { ...c, qty: Math.max(1, c.qty + delta) } : c)));
  const removeItem = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));

  const addVariant = (p, v) =>
    addToCart({
      variant_id: v.id, product_item_id: p.id, name: `${p.name} · ${v.label}`,
      unit_price: v.price, cost: v.cost || 0, is_flower: false, weight_grams: v.weight_grams || 0,
    });

  const addFlower = (f, v) =>
    addToCart({
      variant_id: v.id, product_item_id: f.id, name: `${f.name} · ${v.label}`,
      unit_price: v.price, cost: v.cost || 0, is_flower: true, weight_key: v.label,
      weight_grams: v.weight_grams, tier: f.tier, sub_tier: f.sub_tier, brand: f.brand,
    });

  const onScan = async (e) => {
    if (e.key !== "Enter" || !e.target.value.trim()) return;
    const code = e.target.value.trim();
    e.target.value = "";
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}`);
      if (data.variant) {
        const p = data.product || { id: data.variant.product_item_id, name: data.variant.label };
        addVariant(p, data.variant);
        toast.success("Added to cart");
      } else if (data.product?.variants?.length) {
        addVariant(data.product, data.product.variants[0]);
        toast.success("Added to cart");
      }
    } catch {
      toast.error("No product for that code");
    }
  };

  const filteredProducts = useMemo(() => {
    let list = products;
    if (activeCat !== "all") list = list.filter((p) => p.category === activeCat);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(s) || (p.brand || "").toLowerCase().includes(s));
    }
    return list.filter((p) => p.active !== false && (p.category || "").toLowerCase() !== "flower");
  }, [products, activeCat, q]);

  const showFlower = (activeCat === "all" || activeCat.toLowerCase() === "flower");
  const filteredFlower = useMemo(() => {
    if (!showFlower) return [];
    let list = flower.filter((f) => (f.grams || 0) > 0);
    if (q) list = list.filter((f) => (f.name || "").toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [flower, q, showFlower]);

  const reset = () => { setCart([]); setCustomer(null); setDiscount(null); setHhFree([]); setOrderStarted(false); };

  if (!orderStarted) {
    return (
      <>
        <StartGate onNew={() => openCust("scan")} onReturning={() => openCust("search")} onSkip={() => setOrderStarted(true)} />
        <CustomerDialog open={custOpen} setOpen={setCustOpen} mode={custMode} onSelect={onSelectCustomer} />
      </>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-full">
      {/* CATALOG */}
      <div className="flex-1 min-w-0 flex flex-col p-4 md:p-5 overflow-hidden">
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
              className="pl-9 h-11" data-testid="pos-search-input" />
          </div>
          <div className="relative w-48 hidden sm:block">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input ref={scanRef} onKeyDown={onScan} placeholder="Scan barcode…"
              className="pl-9 h-11" data-testid="pos-scan-input" />
          </div>
        </div>

        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
          <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} label="All" />
          {categories.map((c) => (
            <CatChip key={c.id} active={activeCat === c.name} onClick={() => setActiveCat(c.name)} label={c.name} />
          ))}
        </div>

        <div className="flex-1 overflow-auto pr-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredFlower.map((f) => (
              <FlowerCard key={f.id} f={f} onAdd={(v) => addFlower(f, v)} />
            ))}
            {filteredProducts.map((p) => (
              <ProductCard key={p.id} p={p} onAdd={addVariant} />
            ))}
            {filteredFlower.length === 0 && filteredProducts.length === 0 && (
              <div className="col-span-full text-center text-slate-400 py-16 text-sm">
                No products. Add products in the Products page or transfer flower from the Safe.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CART */}
      <div className="w-full lg:w-[400px] xl:w-[440px] bg-white border-l border-slate-200 flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-slate-200">
          <CustomerPanel customer={customer} onChange={() => openCust("search")} onClear={() => setCustomer(null)} />
          <CustomerDialog open={custOpen} setOpen={setCustOpen} mode={custMode} onSelect={onSelectCustomer} />
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-2">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <ShoppingCart className="w-10 h-10 mb-2" />
              <p className="text-sm">Cart is empty</p>
            </div>
          ) : cart.map((it, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5" data-testid={`cart-item-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{it.name}</div>
                <div className="text-xs text-slate-500">{money(it.unit_price)} ea{hhFree.includes(it.variant_id) ? " · FREE (Happy Hour)" : ""}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setQty(i, -1)} className="w-7 h-7 rounded bg-white border flex items-center justify-center" data-testid={`cart-dec-${i}`}><Minus className="w-3.5 h-3.5" /></button>
                <span className="w-6 text-center text-sm font-semibold">{it.qty}</span>
                <button onClick={() => setQty(i, 1)} className="w-7 h-7 rounded bg-white border flex items-center justify-center" data-testid={`cart-inc-${i}`}><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="w-16 text-right text-sm font-semibold">{money(it.unit_price * it.qty)}</div>
              <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <DiscountButton discount={discount} setDiscount={setDiscount} />
            {totals.happy_hour_active && totals.happy_hour_eligible?.length > 0 && (
              <HappyHourButton eligible={totals.happy_hour_eligible} hhFree={hhFree} setHhFree={setHhFree} />
            )}
          </div>

          <div className="text-sm space-y-1 pt-1">
            <Row label="Subtotal" value={money(totals.subtotal)} />
            {totals.split_oz_discount > 0 && <Row label="Split Oz" value={`-${money(totals.split_oz_discount)}`} green />}
            {totals.discount_amount > 0 && <Row label={`Discount${discount?.reason ? ` (${discount.reason})` : ""}`} value={`-${money(totals.discount_amount)}`} green />}
            {hhAmount > 0 && <Row label="Happy Hour" value={`-${money(hhAmount)}`} green />}
            <div className="flex justify-between text-lg font-bold text-slate-900 pt-1">
              <span>Total</span><span data-testid="cart-total">{money(finalTotal)}</span>
            </div>
          </div>

          <PayoutSection cart={cart} customer={customer} discount={discount} hhFree={hhFree}
            total={finalTotal} drawer={drawer} onDone={() => { reset(); load(); }} />

          <a href={`/display/${DISPLAY_TOKEN}`} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-indigo-600 pt-1" data-testid="open-customer-display">
            <ExternalLink className="w-3 h-3" /> Open customer display
          </a>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, green }) => (
  <div className="flex justify-between">
    <span className="text-slate-500">{label}</span>
    <span className={green ? "text-emerald-600 font-medium" : "text-slate-700"}>{value}</span>
  </div>
);

const CatChip = ({ active, onClick, label }) => (
  <button onClick={onClick} data-testid={`cat-${label.toLowerCase()}`}
    className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${active ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-indigo-300"}`}>
    {label}
  </button>
);

function ProductCard({ p, onAdd }) {
  const v = p.variants || [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 hover:border-indigo-400 hover:shadow-sm transition-all" data-testid={`product-card-${p.id}`}>
      <div className="text-sm font-semibold text-slate-800 leading-tight line-clamp-2">{p.name}</div>
      <div className="text-xs text-slate-400 mb-2">{p.brand}</div>
      {v.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {v.filter((x) => x.active !== false).map((x) => (
            <button key={x.id} onClick={() => onAdd(p, x)} data-testid={`add-variant-${x.id}`}
              className="px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-xs hover:bg-indigo-50 hover:border-indigo-300">
              {x.label} · {money(x.price)}
            </button>
          ))}
        </div>
      ) : (
        <button onClick={() => onAdd(p, { id: p.id, label: "Each", price: p.price, cost: p.cost })}
          className="w-full px-2 py-1.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100" data-testid={`add-product-${p.id}`}>
          Add · {money(p.price)}
        </button>
      )}
    </div>
  );
}

function FlowerCard({ f, onAdd }) {
  const byLabel = Object.fromEntries((f.variants || []).map((v) => [v.label, v]));
  return (
    <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-xl p-3 hover:border-indigo-400 hover:shadow-sm transition-all" data-testid={`flower-card-${f.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-800 leading-tight line-clamp-1">{f.name}</div>
        {f.strain_type && <Badge variant="secondary" className="text-[10px] capitalize">{f.strain_type}</Badge>}
      </div>
      <div className="text-xs text-slate-400 mb-2">{(f.grams || 0).toFixed(0)}g · {f.tier} · THCa {f.thca || 0}%</div>
      <div className="grid grid-cols-3 gap-1">
        {FLOWER_WEIGHTS.map((wk) => {
          const v = byLabel[wk];
          if (!v) return null;
          return (
            <button key={wk} onClick={() => onAdd(v)} data-testid={`add-flower-${f.id}-${wk}`}
              className="px-1 py-1 rounded-md bg-slate-50 border border-slate-200 text-[11px] hover:bg-indigo-50 hover:border-indigo-300">
              <div className="font-semibold">{wk}</div>
              <div className="text-slate-500">{money(v.price)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StartGate({ onNew, onReturning, onSkip }) {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-slate-50 p-6" data-testid="start-order-gate">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Start a new order</h1>
        <p className="text-slate-500 mt-1">Who's shopping today?</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
        <button onClick={onNew} data-testid="gate-new-customer-btn"
          className="group rounded-2xl border-2 border-slate-200 bg-white p-8 text-center hover:border-indigo-500 hover:shadow-md transition-all">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:text-white transition-colors"><ScanLine className="w-8 h-8" /></div>
          <div className="text-lg font-semibold text-slate-900">New Customer</div>
          <div className="text-sm text-slate-500 mt-1">Scan their ID to verify age & create a profile</div>
        </button>
        <button onClick={onReturning} data-testid="gate-returning-customer-btn"
          className="group rounded-2xl border-2 border-slate-200 bg-white p-8 text-center hover:border-purple-500 hover:shadow-md transition-all">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center mb-4 group-hover:bg-purple-600 group-hover:text-white transition-colors"><Users className="w-8 h-8" /></div>
          <div className="text-lg font-semibold text-slate-900">Returning Customer</div>
          <div className="text-sm text-slate-500 mt-1">Search your customer list by name</div>
        </button>
      </div>
      <button onClick={onSkip} data-testid="gate-skip-btn" className="mt-6 text-sm text-slate-400 hover:text-slate-600 underline">Continue without a customer (walk-in)</button>
    </div>
  );
}

function CustomerPanel({ customer, onChange, onClear }) {
  if (customer) {
    const is21 = (customer.age || 0) >= 21;
    return (
      <div className="flex items-start gap-3" data-testid="cart-customer">
        <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0"><User className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800 truncate">{customer.name}</span>
            <Badge className={is21 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"} data-testid="customer-age-badge">
              <BadgeCheck className="w-3 h-3 mr-0.5" />{is21 ? "21+" : `${customer.age}`}
            </Badge>
            {customer.loyalty && <Badge className="bg-amber-100 text-amber-700">Loyalty</Badge>}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span className="inline-flex items-center gap-1"><CircleDollarSign className="w-3 h-3" />{money(customer.lifetime_spend)}</span>
            <span>{customer.visits || 0} visits</span>
            {customer.birthday && <span className="inline-flex items-center gap-1"><Cake className="w-3 h-3" />{customer.birthday}</span>}
          </div>
        </div>
        <button onClick={onChange} className="text-slate-400 hover:text-indigo-600" data-testid="change-customer-btn"><Users className="w-4 h-4" /></button>
        <button onClick={onClear} className="text-slate-400 hover:text-red-500" data-testid="clear-customer-btn"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <Button variant="outline" onClick={onChange} className="w-full justify-start h-10" data-testid="add-customer-btn">
      <UserPlus className="w-4 h-4 mr-2" /> Add customer
    </Button>
  );
}

function CustomerDialog({ open, setOpen, mode = "search", onSelect }) {
  const [view, setView] = useState(mode);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [scanRaw, setScanRaw] = useState("");
  const [form, setForm] = useState(EMPTY_CUST);

  useEffect(() => { if (open) { setView(mode); setQ(""); setScanRaw(""); setForm(EMPTY_CUST); } }, [open, mode]);

  useEffect(() => {
    if (!open || view !== "search") return;
    const t = setTimeout(() => api.get("/customers", { params: { q } }).then((r) => setResults(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q, open, view]);

  const doScan = async () => {
    try {
      const { data } = await api.post("/customers/scan-id", { raw: scanRaw });
      if (data.existing) { onSelect(data.existing); toast.success("Customer matched by ID"); return; }
      setForm((f) => ({ ...f, name: data.parsed.name || "", dob: data.parsed.dob || "", id_number: data.parsed.id_number || "" }));
      setView("create");
      toast.success(`Parsed ID · Age ${data.parsed.age ?? "?"}`);
    } catch { toast.error("Could not parse ID"); }
  };

  const create = async () => {
    try {
      const { data } = await api.post("/customers", form);
      onSelect(data); toast.success("Customer created");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const titles = { search: "Returning customer", scan: "New customer · Scan ID", create: "New customer" };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{titles[view]}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Look up or add a customer for this order.</DialogDescription>

        {view === "search" && (
          <div className="space-y-3">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…" data-testid="customer-search-input" autoFocus />
            <div className="max-h-56 overflow-auto space-y-1">
              {results.map((c) => (
                <button key={c.id} onClick={() => onSelect(c)} data-testid={`customer-result-${c.id}`}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 flex justify-between items-center">
                  <span className="text-sm font-medium">{c.name}</span>
                  <span className="text-xs text-slate-400">{c.phone}</span>
                </button>
              ))}
              {results.length === 0 && <div className="text-xs text-slate-400 text-center py-4">No matches</div>}
            </div>
            <div className="flex justify-between border-t pt-3 text-sm">
              <button onClick={() => setView("scan")} className="text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid="switch-to-scan-btn"><ScanLine className="w-4 h-4" />Scan ID for a new customer</button>
              <button onClick={() => setView("create")} className="text-slate-500 hover:underline" data-testid="switch-to-manual-btn">Enter manually</button>
            </div>
          </div>
        )}

        {view === "scan" && (
          <div className="space-y-3">
            <Label className="text-xs text-slate-500">Scan or paste the driver license barcode (AAMVA / PDF417)</Label>
            <Textarea value={scanRaw} onChange={(e) => setScanRaw(e.target.value)} placeholder="Focus here and scan the back of the ID…" rows={4} data-testid="customer-scan-input" autoFocus />
            <Button onClick={doScan} className="w-full" data-testid="customer-scan-btn"><ScanLine className="w-4 h-4 mr-1.5" />Scan &amp; verify age</Button>
            <div className="flex justify-between text-sm">
              <button onClick={() => setView("search")} className="text-slate-500 hover:underline" data-testid="switch-to-search-btn">Returning customer?</button>
              <button onClick={() => setView("create")} className="text-slate-500 hover:underline" data-testid="switch-to-manual-btn-2">Enter manually</button>
            </div>
          </div>
        )}

        {view === "create" && (
          <div className="space-y-2.5">
            {[["name", "Full name"], ["dob", "DOB (YYYY-MM-DD)"], ["phone", "Phone"], ["email", "Email"], ["id_number", "ID number"]].map(([k, lbl]) => (
              <div key={k}><Label className="text-xs">{lbl}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`new-customer-${k}`} /></div>
            ))}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.loyalty} onChange={(e) => setForm({ ...form, loyalty: e.target.checked })} data-testid="new-customer-loyalty" /> Loyalty member</label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setView(mode === "scan" ? "scan" : "search")}>Back</Button>
              <Button onClick={create} data-testid="save-customer-btn">Save customer</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DiscountButton({ discount, setDiscount }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("flat");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const apply = () => {
    if (!value || !reason.trim()) { toast.error("Amount and reason required"); return; }
    setDiscount({ type, value: parseFloat(value), reason });
    setOpen(false);
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="flex-1" data-testid="discount-btn">
        <Tag className="w-3.5 h-3.5 mr-1.5" />{discount ? `${discount.type === "flat" ? money(discount.value) : discount.value + "%"} off` : "Discount"}
      </Button>
      {discount && <Button variant="ghost" size="sm" onClick={() => setDiscount(null)} data-testid="clear-discount-btn"><X className="w-4 h-4" /></Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Apply discount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="discount-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat dollar ($)</SelectItem>
                <SelectItem value="percent">Percentage (%)</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "flat" ? "5.00" : "10"} data-testid="discount-value-input" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="discount-reason-input" />
          </div>
          <DialogFooter><Button onClick={apply} data-testid="apply-discount-btn">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function HappyHourButton({ eligible, hhFree, setHhFree }) {
  const [open, setOpen] = useState(false);
  const toggle = (ref) => setHhFree((prev) => (prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]));
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" data-testid="happy-hour-btn">
        <Zap className="w-3.5 h-3.5 mr-1.5" />Happy Hour{hhFree.length ? ` (${hhFree.length})` : ""}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Happy Hour freebies</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Select which eligible items get a Happy Hour freebie (one free gram and/or one house gummie).</p>
          <div className="space-y-1.5">
            {eligible.map((e) => (
              <label key={e.ref_id} className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer" data-testid={`hh-item-${e.ref_id}`}>
                <input type="checkbox" checked={hhFree.includes(e.ref_id)} onChange={() => toggle(e.ref_id)} />
                <span className="text-sm">{e.name} <span className="text-xs text-slate-400">({e.kind})</span></span>
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={() => setOpen(false)} data-testid="hh-confirm-btn">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PayoutSection({ cart, customer, discount, hhFree, total, drawer, onDone }) {
  const [payment, setPayment] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [cashAmt, setCashAmt] = useState("");
  const [cardAmt, setCardAmt] = useState("");
  const [prizeCode, setPrizeCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [labelOpen, setLabelOpen] = useState(false);
  const [labeled, setLabeled] = useState({});

  const isPrize = payment === "prize";
  const change = payment === "cash" && tendered ? Math.max(0, parseFloat(tendered) - total) : 0;
  const splitOk = payment !== "split" || (parseFloat(cashAmt || 0) + parseFloat(cardAmt || 0)).toFixed(2) === total.toFixed(2);
  const needsDrawer = (payment === "cash" || payment === "split") && !drawer;

  const checkout = async () => {
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    if (needsDrawer) { toast.error("Open a cash drawer first"); return; }
    if (payment === "cash" && parseFloat(tendered || 0) < total) { toast.error("Insufficient cash tendered"); return; }
    if (!splitOk) { toast.error("Split amounts must equal the total"); return; }
    setBusy(true);
    try {
      const body = {
        items: cart, customer_id: customer?.id || null, discount: discount || undefined,
        payment_type: payment, cash_tendered: parseFloat(tendered || 0),
        cash_amount: parseFloat(cashAmt || 0), card_amount: parseFloat(cardAmt || 0),
        happy_hour_free: hhFree, prize_code: prizeCode || null,
      };
      const { data } = await api.post("/sales", body);
      setDone(data);
      toast.success(isPrize ? "Prize logged" : "Sale complete");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const finish = () => { setDone(null); setLabelOpen(false); setLabeled({}); setTendered(""); setCashAmt(""); setCardAmt(""); setPrizeCode(""); setPayment("cash"); onDone(); };
  const reprint = () => api.post(`/sales/${done.id}/reprint`).then(() => toast.success("Reprint sent")).catch((e) => toast.error(formatApiError(e)));
  const printLabel = (index) => api.post(`/sales/${done.id}/print-label`, { item_index: index }).then((r) => { if (r.data?.ok) { toast.success("Label sent"); setLabeled((m) => ({ ...m, [index]: true })); } else { toast.info(r.data?.error || "Nothing to label"); } }).catch((e) => toast.error(formatApiError(e)));
  const hasFlower = (done?.items || []).some((it) => it.is_flower);
  const flowerItems = (done?.items || []).map((it, i) => ({ ...it, _idx: i })).filter((it) => it.is_flower);

  return (
    <div className="space-y-2">
      <Select value={payment} onValueChange={setPayment}>
        <SelectTrigger className="h-11" data-testid="payment-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(PAYMENT_LABELS).map(([k, v]) => (
            <SelectItem key={k} value={k} data-testid={`payment-opt-${k}`}>{v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {payment === "cash" && (
        <div className="flex items-center gap-2">
          <Input type="number" value={tendered} onChange={(e) => setTendered(e.target.value)} placeholder="Cash tendered" className="h-11" data-testid="cash-tendered-input" />
          <div className="text-sm whitespace-nowrap">Change <span className="font-semibold" data-testid="cash-change">{money(change)}</span></div>
        </div>
      )}
      {payment === "credit" && <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 text-center">Follow the prompt on the PAX terminal.</div>}
      {payment === "split" && (
        <div className="flex gap-2">
          <Input type="number" value={cashAmt} onChange={(e) => setCashAmt(e.target.value)} placeholder="Cash" className="h-11" data-testid="split-cash-input" />
          <Input type="number" value={cardAmt} onChange={(e) => setCardAmt(e.target.value)} placeholder="Card" className="h-11" data-testid="split-card-input" />
        </div>
      )}
      {isPrize && (
        <>
          <div className="text-xs bg-amber-50 text-amber-700 rounded-lg p-2 text-center border border-amber-200">PRIZE · $0.00 · inventory still deducts, logged for accountability.</div>
          <Input value={prizeCode} onChange={(e) => setPrizeCode(e.target.value)} placeholder="Prize code (optional)" className="h-10" data-testid="prize-code-input" />
        </>
      )}
      {(payment === "website" || payment === "doordash") && (
        <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 text-center">No cash-drawer impact · prints {payment === "website" ? "WEBSITE" : "DOORDASH"} banner.</div>
      )}

      {needsDrawer && <div className="text-xs text-red-600 text-center">Open a cash drawer to accept cash/split.</div>}

      <Button onClick={checkout} disabled={busy || cart.length === 0} className={`w-full h-12 text-white font-semibold ${isPrize ? "bg-amber-600 hover:bg-amber-700" : gradient + " hover:opacity-90"}`} data-testid="checkout-btn">
        {busy ? "Processing…" : isPrize ? "Log Prize" : `Charge ${money(total)}`}
      </Button>

      <Dialog open={!!done} onOpenChange={(v) => !v && finish()}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            {done?.is_prize ? <><Gift className="w-5 h-5 text-amber-600" /> Prize logged</> : "Sale complete"}
          </DialogTitle></DialogHeader>
          {done && (
            <div className="space-y-3">
              <div className="text-center py-2">
                <div className="text-3xl font-bold text-slate-900">{money(done.total)}</div>
                <div className="text-sm text-slate-500">Receipt #{done.receipt_no} · {PAYMENT_LABELS[done.payment_type]}</div>
                {done.change > 0 && <div className="text-sm text-emerald-600 mt-1">Change due {money(done.change)}</div>}
              </div>
              <div className={`grid gap-2 ${hasFlower ? "grid-cols-2" : "grid-cols-1"}`}>
                <Button variant="outline" onClick={reprint} data-testid="postsale-reprint-btn"><Printer className="w-4 h-4 mr-1.5" />Reprint</Button>
                {hasFlower && <Button variant="outline" onClick={() => setLabelOpen(true)} data-testid="postsale-label-btn"><Tag className="w-4 h-4 mr-1.5" />Label</Button>}
              </div>
              <Button onClick={finish} className="w-full" data-testid="postsale-done-btn">New sale</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={labelOpen} onOpenChange={setLabelOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-emerald-600" /> Print labels</DialogTitle>
            <DialogDescription>Print a label for each strain in this sale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="label-picker-list">
            {flowerItems.map((it) => (
              <div key={it._idx} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-200" data-testid={`label-row-${it._idx}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{it.name}</div>
                  <div className="text-xs text-slate-500">Qty {it.qty || 1}</div>
                </div>
                <Button size="sm" variant={labeled[it._idx] ? "secondary" : "outline"} onClick={() => printLabel(it._idx)} data-testid={`label-print-btn-${it._idx}`} className="shrink-0">
                  {labeled[it._idx] ? <><BadgeCheck className="w-4 h-4 mr-1.5" />Labeled</> : <><Tag className="w-4 h-4 mr-1.5" />Label</>}
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLabelOpen(false)} className="w-full" data-testid="label-picker-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
